import { useEffect } from 'react';
import { useGetLanguagesQuery, type LanguageOption } from '../api/generated';

const PLAINTEXT: LanguageOption = { id: 'plaintext', label: 'Plain text', executable: false };

/** How long to wait before asking again after the catalog failed to load (e.g. backend restarting). */
export const LANGUAGES_RETRY_MS = 3000;

export interface Languages {
  readonly options: readonly LanguageOption[];
  readonly defaultLanguage: string;
  readonly isLoading: boolean;
  /** The last attempt to load the catalog failed; another one is scheduled. */
  readonly loadFailed: boolean;
  /** Asks again right away. */
  readonly retry: () => void;
  /** Human-readable name of a language id, falling back to the id itself. */
  readonly labelOf: (id: string) => string;
  /** Whether pastes in this language can be executed on this deployment. */
  readonly isExecutable: (id: string) => boolean;
}

/**
 * The backend's language catalog, with a plain-text fallback until (or unless) it loads. A failed
 * load is retried every {@code retryMs} until it succeeds, so a transient outage does not leave
 * the page stuck on the fallback.
 */
export function useLanguages(retryMs: number = LANGUAGES_RETRY_MS): Languages {
  const { data, isLoading, isError, isFetching, refetch } = useGetLanguagesQuery();

  useEffect(() => {
    if (!isError || isFetching) {
      return undefined;
    }
    const timer = setTimeout(() => void refetch(), retryMs);
    return () => clearTimeout(timer);
  }, [isError, isFetching, refetch, retryMs]);

  const options = data?.languages ?? [PLAINTEXT];
  return {
    options,
    defaultLanguage: data?.default ?? PLAINTEXT.id,
    isLoading,
    loadFailed: isError && data === undefined,
    retry: () => void refetch(),
    labelOf: (id) => options.find((option) => option.id === id)?.label ?? id,
    isExecutable: (id) => options.find((option) => option.id === id)?.executable ?? false,
  };
}
